// Week maths for the timesheet grid.
//
// Mirrors back/services/timesheet.js on purpose — the grid has to lay out the
// same seven columns the server will accept hours for, and the two agreeing
// by accident is how a Sunday ends up written into next week.
//
// Everything is a UTC midnight. A cell is a DAY, not a moment: an operator in
// Phoenix typing "6" into Monday must hit the same cell the server calls
// Monday, and a local-midnight Date does not survive that trip.
//
// Pure and exported so the column layout and the totals are testable without
// rendering anything.

export const DAY_MS = 24 * 60 * 60 * 1000;
export const MAX_HOURS_PER_DAY = 24;

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function dayKey(d) {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate()));
}

// Monday of the week containing `d`. Sunday closes the week it is in.
export function startOfWeek(d) {
  const day = dayKey(d);
  if (!day) return null;
  const dow = day.getUTCDay();
  return new Date(day.getTime() - (dow === 0 ? 6 : dow - 1) * DAY_MS);
}

export function weekDates(d) {
  const monday = startOfWeek(d);
  if (!monday) return [];
  return Array.from({ length: 7 }, (_, i) => new Date(monday.getTime() + i * DAY_MS));
}

export function addWeeks(d, n) {
  const monday = startOfWeek(d);
  return monday ? new Date(monday.getTime() + n * 7 * DAY_MS) : null;
}

export function sameDay(a, b) {
  const x = dayKey(a); const y = dayKey(b);
  return !!x && !!y && x.getTime() === y.getTime();
}

// "Mon 11" — the column header, matching the source grid.
export function columnLabel(d) {
  const day = dayKey(d);
  if (!day) return '';
  return `${DAY_NAMES[day.getUTCDay()]} ${day.getUTCDate()}`;
}

// "11 Aug – 17 Aug 2026" for the week picker.
export function weekLabel(d) {
  const days = weekDates(d);
  if (!days.length) return '';
  const fmt = (x, withYear) => `${x.getUTCDate()} ${x.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}${withYear ? ` ${x.getUTCFullYear()}` : ''}`;
  return `${fmt(days[0], false)} – ${fmt(days[6], true)}`;
}

export function isoDay(d) {
  const day = dayKey(d);
  return day ? day.toISOString() : '';
}

// Hours an entry has on one day (0 when the day is untouched).
export function hoursFor(entry, date) {
  const hit = ((entry && entry.dates) || []).find((x) => sameDay(x.date, date));
  return hit ? Number(hit.hours) || 0 : 0;
}

export function noteFor(entry, weekAnchor) {
  const days = weekDates(weekAnchor);
  const hit = ((entry && entry.notes) || []).find((n) => days.some((w) => sameDay(w, n.date)));
  return hit ? hit.note || '' : '';
}

export function weekTotal(entry, weekAnchor) {
  return weekDates(weekAnchor).reduce((s, d) => s + hoursFor(entry, d), 0);
}

// The footer row: one total per day across every visible row.
export function dayTotals(entries, weekAnchor) {
  return weekDates(weekAnchor).map((date) => ({
    date,
    hours: (entries || []).reduce((s, e) => s + hoursFor(e, date), 0),
  }));
}

// Which days would break the 24-hour ceiling if `draft` were saved. Returned
// as a list rather than a boolean so the grid can mark the offending column
// instead of just refusing the save.
export function overCapDays(entries, draftByEntryId, weekAnchor) {
  const out = [];
  for (const date of weekDates(weekAnchor)) {
    const total = (entries || []).reduce((s, e) => {
      const draft = draftByEntryId && draftByEntryId[e._id];
      if (draft && draft.hours && draft.hours[isoDay(date)] != null) {
        return s + (Number(draft.hours[isoDay(date)]) || 0);
      }
      return s + hoursFor(e, date);
    }, 0);
    if (total > MAX_HOURS_PER_DAY) out.push({ date, hours: total });
  }
  return out;
}
